import React, {
  useCallback,
  useEffect,
  useState,
  ReactNode,
  Suspense,
} from "react";
import Drawer, { IDrawerProps } from "../Core/Drawer/Drawer";

interface ChildrenProps {
  onOpen: () => void;
  onClose: () => void;
}

type EmitedDrawerProps = Omit<
  IDrawerProps,
  "open" | "onClose" | "maskClosable" | "placement" | "push"
>;

export interface IWithDrawerProps {
  children?: (data: ChildrenProps) => React.ReactElement;
  onClose?: (manual?: boolean) => void;
  onOpen?: (manual?: boolean) => void;
  onEmitDrawerProps?: (data: EmitedDrawerProps) => void;
  open?: boolean;
  [_: string]: any;
  disableAnimation?: boolean;
  drawerClassName?: string;
}

export type IDrawerActions = {
  open(hash?: string): void;
  close(): void;
};

type IDrawerExtraProps = {
  extraContent?: (open: boolean, actions: IDrawerActions) => ReactNode;
};

const withDrawer = <Props extends IWithDrawerProps>(
  Component: (props: Props) => ReactNode,
  drawerProps?: Omit<IDrawerProps, "open" | "onClose"> & IDrawerExtraProps
): React.ComponentType<IWithDrawerProps & Props> => {
  return (props: Props) => {
    const {
      children,
      onClose,
      open: openDefault,
      extraContent: mainExtraContent,
      disableAnimation,
      drawerClassName,
    } = props;
    const {
      width = 630,
      contentWrapperStyle,
      extraContent,
      ...restDrawerProps
    } = drawerProps || {};
    // const [mounted, setMounted] = useState(false);
    const [open, setOpen] = useState(openDefault || false);
    const [drawerControlledProps, setDrawerControlledProps] =
      useState<EmitedDrawerProps>({});
    const onOpen = () => setOpen(true);
    const onInnerClose = () => setOpen(false);
    const handleClose = useCallback(
      (manual?: boolean) => {
        onClose && onClose(manual);
        onInnerClose();
      },
      [onClose]
    );

    const handleEmitDrawerProps = useCallback(
      (drawerProps: EmitedDrawerProps) => {
        setDrawerControlledProps(drawerProps);
      },
      []
    );

    useEffect(() => {
      if (openDefault !== undefined && openDefault !== open) {
        setOpen(openDefault);
      }
    }, [openDefault]);

    const {
      width: emitedWidth,
      contentWrapperStyle: emitedContentWrapperStyle,
      ...restEmitedProps
    } = drawerControlledProps;

    return (
      <>
        {children && children({ onOpen, onClose: onInnerClose })}
        {extraContent?.(open, {
          open: onOpen,
          close: handleClose,
        })}
        {mainExtraContent?.(open, {
          open: onOpen,
          close: handleClose,
        })}
        {!!open && (
          <Drawer
            width="100%"
            contentWrapperStyle={{
              ...contentWrapperStyle,
              ...emitedContentWrapperStyle,
              maxWidth: emitedWidth || width,
            }}
            open={open}
            maskClosable
            onClose={() => handleClose()}
            placement="right"
            push={false}
            forceRender={restDrawerProps.forceRender}
            destroyOnClose={true}
            disableAnimation={disableAnimation}
            rootClassName={drawerClassName}
            {...restDrawerProps}
            {...restEmitedProps}
          >
            {!drawerProps?.forceRender && !open ? null : (
              <Suspense>
                {/* @ts-ignore */}
                <Component
                  onEmitDrawerProps={handleEmitDrawerProps}
                  {...props}
                  onClose={handleClose}
                />
              </Suspense>
            )}
          </Drawer>
        )}
      </>
    );
  };
};

export default withDrawer;
